import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import { appAnalyticsAPI } from '../../services/api';
import { AppNav, AppStyles, fmtDate, fmtDuration, fmtMoney, fmtNum, platformIcon } from './common';

const eventIcon = (e: any) => {
  if (e.type === 'screen') return { icon: 'bi-window', color: '#0ea5e9' };
  if (e.type === 'identify') return { icon: 'bi-person-check', color: '#6366f1' };
  if (e.event === 'first_open') return { icon: 'bi-download', color: '#6366f1' };
  if (e.event === 'app_open') return { icon: 'bi-box-arrow-in-right', color: '#64748b' };
  if (e.event === 'app_background') return { icon: 'bi-box-arrow-left', color: '#94a3b8' };
  if (e.milestone?.is_conversion) return { icon: 'bi-trophy', color: '#10b981' };
  if (e.milestone) return { icon: 'bi-flag', color: '#8b5cf6' };
  return { icon: 'bi-lightning', color: '#f59e0b' };
};

const timeOf = (d: any) => new Date(d).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });

const AppUserTimelinePage: React.FC = () => {
  const { id } = useParams();
  const { currentAsset } = useAuth();
  const assetId = currentAsset?.id;
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showSystem, setShowSystem] = useState(true);
  const [openProps, setOpenProps] = useState<number | null>(null);

  useEffect(() => {
    if (!assetId || !id) return;
    setLoading(true);
    appAnalyticsAPI.getUserTimeline(assetId, id)
      .then(r => { setData(r.data?.data); setError(''); })
      .catch(e => setError(e?.response?.data?.message || 'Could not load this user'))
      .finally(() => setLoading(false));
  }, [assetId, id]);

  const p = data?.profile;
  const reachedCount = (data?.lifecycle || []).filter((s: any) => s.reached_at).length;

  return (
    <DashboardLayout>
      <AppStyles />
      <div className="container-fluid py-4">
        <Link to="/app-analytics/users" className="small text-decoration-none"><i className="bi bi-arrow-left me-1"></i>All users</Link>
        <AppNav />
        {loading && <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>}
        {error && <div className="alert alert-danger">{error}</div>}
        {data && p && (
          <div className="row g-3">
            <div className="col-xl-4">
              <div className="card border-0 shadow-sm mb-3">
                <div className="card-body">
                  <div className="d-flex align-items-center gap-3 mb-3">
                    <span className="d-inline-flex align-items-center justify-content-center rounded-circle bg-primary-subtle text-primary" style={{ width: 48, height: 48 }}>
                      <i className={`bi ${p.identified ? 'bi-person-fill' : 'bi-incognito'} fs-4`}></i>
                    </span>
                    <div>
                      <div className="fw-bold fs-5">{p.identified ? p.user_id : 'Anonymous user'}</div>
                      <div className="small text-muted text-break">{p.anonymous_ids.length > 1 ? `${p.anonymous_ids.length} devices` : p.anonymous_ids[0]}</div>
                    </div>
                  </div>
                  <div className="row g-2 small">
                    <div className="col-6"><div className="text-muted">Stage</div><div className="fw-semibold">{p.stage?.display_name || '—'}</div></div>
                    <div className="col-6"><div className="text-muted">Device</div><div className="fw-semibold"><i className={`bi ${platformIcon(p.platform)} me-1`}></i>{p.device_model || p.platform || '—'}</div></div>
                    <div className="col-6"><div className="text-muted">App version</div><div className="fw-semibold">{p.app_version || '—'}</div></div>
                    <div className="col-6"><div className="text-muted">OS</div><div className="fw-semibold">{[p.os, p.os_version].filter(Boolean).join(' ') || '—'}</div></div>
                    <div className="col-6"><div className="text-muted">Country</div><div className="fw-semibold">{p.country || '—'}</div></div>
                    <div className="col-6"><div className="text-muted">Sessions</div><div className="fw-semibold">{fmtNum(p.sessions)}</div></div>
                    <div className="col-6"><div className="text-muted">Purchases</div><div className="fw-semibold">{fmtNum(p.purchases)} · {fmtMoney(p.revenue)}</div></div>
                    <div className="col-6"><div className="text-muted">Events</div><div className="fw-semibold">{fmtNum(p.events)}</div></div>
                    <div className="col-6"><div className="text-muted">First seen</div><div>{fmtDate(p.first_seen_at)}</div></div>
                    <div className="col-6"><div className="text-muted">Last seen</div><div>{fmtDate(p.last_seen_at)}</div></div>
                  </div>
                  {Object.keys(p.traits || {}).length > 0 && (
                    <div className="mt-3 small">
                      <div className="text-muted mb-1">Traits</div>
                      {Object.entries(p.traits).map(([k, v]) => <div key={k}><code>{k}</code>: {typeof v === 'object' ? JSON.stringify(v) : String(v)}</div>)}
                    </div>
                  )}
                </div>
              </div>

              <div className="card border-0 shadow-sm">
                <div className="card-body">
                  <h6 className="fw-bold mb-1">Lifecycle progress</h6>
                  <div className="small text-muted mb-2">{reachedCount} of {data.lifecycle.length} milestones reached</div>
                  <ol className="list-unstyled mb-0">
                    {data.lifecycle.map((s: any, i: number) => (
                      <li key={s.step_key} className="d-flex align-items-start gap-2 py-1">
                        <i className={`bi ${s.reached_at ? (s.is_conversion ? 'bi-trophy-fill text-success' : 'bi-check-circle-fill text-primary') : 'bi-circle text-muted'} mt-1`}></i>
                        <div className="small">
                          <div className={s.reached_at ? 'fw-semibold' : 'text-muted'}>{i + 1}. {s.display_name}</div>
                          {s.reached_at && <div className="text-muted" style={{ fontSize: 11 }}>{fmtDate(s.reached_at)}</div>}
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
              </div>
            </div>

            <div className="col-xl-8">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="fw-bold mb-0">Journey timeline <span className="text-muted fw-normal small">· newest session first</span></h6>
                <div className="form-check form-switch small m-0">
                  <input className="form-check-input" type="checkbox" id="sys" checked={showSystem} onChange={e => setShowSystem(e.target.checked)} />
                  <label className="form-check-label" htmlFor="sys">Show app open/background</label>
                </div>
              </div>
              {data.truncated && <div className="alert alert-info small py-2">Showing the most recent 2,000 events.</div>}
              {data.sessions.length === 0 && <div className="text-muted">No events.</div>}
              {data.sessions.map((s: any, si: number) => (
                <div key={s.session_id || 'none'} className="card border-0 shadow-sm mb-3">
                  <div className="card-header bg-white d-flex flex-wrap gap-3 small align-items-center">
                    <span className="fw-semibold"><i className="bi bi-collection-play me-1 text-primary"></i>Session {data.sessions.length - si}</span>
                    <span className="text-muted">{fmtDate(s.started_at)}</span>
                    {s.duration_sec !== null && <span className="text-muted"><i className="bi bi-stopwatch me-1"></i>{fmtDuration(s.duration_sec)}</span>}
                    <span className="text-muted">{s.events.length} events</span>
                    {s.app_version && <span className="text-muted">v{s.app_version}</span>}
                    {s.events.some((e: any) => e.milestone?.is_conversion) && <span className="badge bg-success">converted</span>}
                  </div>
                  <div className="card-body py-2">
                    <div className="ca-timeline">
                      {s.events.filter((e: any) => showSystem || !['app_open', 'app_background'].includes(e.event)).map((e: any) => {
                        const ic = eventIcon(e);
                        const hasProps = e.properties && Object.keys(e.properties).length > 0;
                        return (
                          <div key={e.id} className={`position-relative mb-2 rounded px-2 py-1 ${e.milestone?.is_conversion ? 'ca-conversion' : e.milestone ? 'ca-milestone' : ''}`}>
                            <span className="ca-tl-dot" style={{ background: ic.color }}></span>
                            <div className="d-flex align-items-center gap-2 flex-wrap small">
                              <span className="text-muted font-monospace" style={{ width: 70 }}>{timeOf(e.timestamp)}</span>
                              <i className={`bi ${ic.icon}`} style={{ color: ic.color }}></i>
                              <span className="fw-semibold">
                                {e.type === 'screen' ? <>Viewed <em>{e.screen}</em></> : e.type === 'identify' ? 'Identified' : (e.milestone?.display_name || e.event)}
                              </span>
                              {e.type === 'track' && e.milestone && <code className="text-muted">{e.event}</code>}
                              {e.milestone?.first_time && <span className={`badge ${e.milestone.is_conversion ? 'bg-success' : 'bg-primary'} ca-stage-pill`}>{e.milestone.is_conversion ? 'Conversion' : 'Milestone'}</span>}
                              {e.revenue !== null && <span className="badge bg-success-subtle text-success">{fmtMoney(e.revenue, e.currency)}</span>}
                              {e.type !== 'screen' && e.screen && <span className="text-muted">on {e.screen}</span>}
                              {hasProps && (
                                <button className="btn btn-link btn-sm p-0 ms-auto small" onClick={() => setOpenProps(openProps === e.id ? null : e.id)}>
                                  {openProps === e.id ? 'hide' : 'properties'}
                                </button>
                              )}
                            </div>
                            {openProps === e.id && hasProps && (
                              <pre className="small bg-light rounded p-2 mt-1 mb-0">{JSON.stringify(e.properties, null, 2)}</pre>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AppUserTimelinePage;
